'use client';

import {
  Chip,
  darken,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import type { SchoolYearKey } from '@ocode/domain';
import { SchoolYear } from '@ocode/domain';
import { FlexItemsContainer, FlexRow } from '@ocodelib/mui';
import { routerPushI18n } from '@repo-ui/router';
import { useLocale } from 'next-intl';

interface Props {
  /**
   * 선택된 학년 KEY
   */
  selectedYearKey: SchoolYearKey;

  /**
   * 선택된 교과서 KEY
   */
  selectedSubjectKey?: string;

  /**
   * 선택 가능한 교과서 KEY 목록
   */
  selectableSubjectKeys: string[];

  /**
   * 선택 가능한 학년 KEY 목록
   */
  selectableYearKeys: SchoolYearKey[];

  /**
   * 모든 교과서 이름들 (name by key)
   */
  allSubjectNames: Record<string, string>;
}

export function SearchBox(props: Props) {
  const {
    selectedSubjectKey,
    selectedYearKey,
    selectableSubjectKeys,
    selectableYearKeys,
    allSubjectNames,
  } = props;
  const locale = useLocale();

  // 학년을 선택
  const handleClickYear = (year: SchoolYearKey) => () => {
    // 해당학년에 선택된 교과서가 없을 수도 있고, 없으면 자동으로 다른 교과서를 선택 한다.
    routerPushI18n('/dtb/list', year, selectedSubjectKey);
  };

  // 교과서를 선택
  const handleClickSubject = (subject: string) => () => {
    routerPushI18n('/dtb/list', selectedYearKey, subject);
  };

  return (
    <TableContainer
      className="SearchBox-root"
      sx={{ borderRadius: '5px', border: '1px solid #ddd' }}
    >
      <Table sx={{ borderStyle: 'hidden' }}>
        <TableBody
          sx={{
            '& .MuiTableCell-root:first-of-type': {
              whiteSpace: 'nowrap',
              minWidth: { xs: 100, sm: 120, md: 140 },
              bgcolor: '#FFF6E4',
              textAlign: 'center',
            },
            '& .MuiTableCell-root': {
              border: '1px solid #ddd',
              bgcolor: '#fff',
              py: 1,
            },
          }}
        >
          <TableRow>
            <TableCell>
              <Typography sx={{ fontSize: '0.9rem', color: '#4E5052' }}>학년</Typography>
            </TableCell>
            <TableCell>
              <FlexItemsContainer spacing={1} items={selectableYearKeys} getItemKey={(it) => it}>
                {(yearKey) => (
                  <Chip
                    className="SearchBox-yearChip"
                    variant={yearKey === selectedYearKey ? 'filled' : 'outlined'}
                    label={SchoolYear[yearKey] ?? yearKey}
                    onClick={handleClickYear(yearKey)}
                    sx={{
                      border: 'none',
                      '&.MuiChip-filledDefault': {
                        bgcolor: 'primary.main',
                        color: '#fff',
                        ':hover': {
                          bgcolor: (theme) => darken(theme.palette.primary.main, 0.2),
                        },
                      },
                      '&.MuiChip-outlinedDefault': {
                        bgcolor: '#F3F5F8',
                        color: '#4E5052',
                        ':hover': {
                          bgcolor: darken('#F3F5F8', 0.1),
                        },
                      },
                      '& > .MuiChip-label': {
                        p: '12px',
                      },
                    }}
                  />
                )}
              </FlexItemsContainer>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography sx={{ fontSize: '0.9rem', color: '#4E5052' }}>교과목</Typography>
            </TableCell>
            <TableCell>
              <FlexItemsContainer spacing={1} items={selectableSubjectKeys} getItemKey={(it) => it}>
                {(it) => (
                  <Chip
                    key={it}
                    className="SearchBox-subjectChip"
                    color="primary"
                    variant={it === selectedSubjectKey ? 'filled' : 'outlined'}
                    label={allSubjectNames[it] ?? it}
                    onClick={handleClickSubject(it)}
                    sx={{
                      border: 'none',
                      '&.MuiChip-filledDefault': {
                        bgcolor: 'primary.main',
                        color: '#fff',
                        ':hover': {
                          bgcolor: (theme) => darken(theme.palette.primary.main, 0.2),
                        },
                      },
                      '&.MuiChip-outlinedDefault': {
                        bgcolor: '#F3F5F8',
                        color: '#000',
                        ':hover': {
                          bgcolor: darken('#F3F5F8', 0.1),
                        },
                      },
                    }}
                  />
                )}
              </FlexItemsContainer>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
